import Box from "@mui/material/Box";
import React, {useEffect, useState} from "react";
import {Page} from "../../routing/types";
import PageGridPlacement from "../layouts/body/PageGridPlacement";
import Navigation from "../layouts/navigation/Navigation";
import Breadcrumb from "../layouts/navigation/Breadcrumb";
import TopNavi from "../layouts/topnavi/TopNavi";
import CCORichText from "../fundamentals/CCORichText/CCORichText";
import CCOContainer from "../fundamentals/CCOContainer";
import CCOComponentWrapper from "../fundamentals/CCOComponentWrapper";
import CCOCartFab from "../fundamentals/CCOCartFab";
import {useCartContext} from "../../context/CartContext";
import {useRouter} from "next/router";
import routes from "../../common/constants/routes";
import {getRouteUrl} from "../../utility/navigationUtil";
import {usePageContext} from "../../context/PageContext";
import CCOH1 from "../fundamentals/CCOH1";
import {PageGridPlacementType} from "../types";
import {
    getPageContentType,
    isCart,
    isOfferPage,
    isProductPage,
    isSpecialTopicPage,
    PageContentType,
    pageGridItemFullWidthSx,
    pageHeaderSx,
    placement_footer,
    placement_header,
    placement_stageteaser,
    placement_topnavi,
    queryList,
    sosEvent,
    sosNews,
    sosPressrelease,
    sosPublication
} from "./pageLayoutsUtils";
import useNavigationHeight from "../../utility/useNavigationHeight";
import PreviewElementBoxDrawer from "../preview/PreviewElementBoxDrawer";
import useWindowSize from "../../utility/useWindowSize";
import dynamic from "next/dynamic";
import P020DonationProduct from "../doctypes/P020DonationProduct/P020DonationProduct";

import type {CAEGlobalSettings} from "../../types/CAEGlobalSettings";
import EasyLangFooter from "../layouts/footer/EasyLangFooter";

interface Props {
    isHideNavi?: boolean,
    navigation: any,
    page: any,
    content?: any,
    CAEGlobalSettings: CAEGlobalSettings,
    children?: JSX.Element,
    currentURL: string,
    csrfToken: string,
    pageSettings?: any,
    credentials?: any,
    donationInitializerData?: { queryData: any; isProduct: boolean; contentId: string } | null
}

const P013News = dynamic(() => import("../doctypes/P013News"));

const P014Event = dynamic(() => import("../doctypes/P014Event"));

const P012Pressrelease = dynamic(() => import("../doctypes/P012Pressrelease"));

const LogoOnlyNavigation = dynamic(() => import("../layouts/navigation/LogoOnlyNavigation"));

const P011Publication = dynamic(() => import("../doctypes/P011Publication/P011Publication"));

const P015Cart = dynamic(() => import("../doctypes/cart/P015Cart"));

const Footer = dynamic(() => import("../layouts/footer/Footer"));

export function checkForSpecialTopics(page: any) {
    let hasSpecialTopicPlaceholder = false;
    page.grid.placements.map((placement: PageGridPlacementType) => {
        placement.items.map((item: any) => {
            if (item.viewtype === "c021_kachel_teaser_with_special_topics") {
                hasSpecialTopicPlaceholder = true;
                return true;
            }
        })
    });
    return hasSpecialTopicPlaceholder;
}

export function checkForQueryList(page: any) {
    return page.grid.placements.filter((placement: PageGridPlacementType) => {
        return placement.items.filter((item: any) => item.type === queryList).length > 0
    }).length > 0
}

export function getPageContent(props: Props) {
    let pageContentType = getPageContentType(props.content?.content?.content?.type, props.currentURL);
    if (isProductPage(props.page)) {
        pageContentType = PageContentType.Product;
    }
    return pageContentType;
}

function HomepageLayout(props: Props) {
    const [shouldRenderCartFab, setShouldRenderCartFab] = useState(false);
    const pageContext = usePageContext();
    const cartLink = getRouteUrl(pageContext.rootUrl, routes.PUBLICATIONS.CART)
    const {greaterThanWindowLarge} = useWindowSize();
    const navHeight = useNavigationHeight(props.isHideNavi || pageContext.hideTopNavi);
    const {isCartFilled} = useCartContext();
    const {asPath} = useRouter();
    const header = props.page?.grid.placements.find((p: PageGridPlacementType) => p.name === placement_header);

    useEffect(() => {
        const showCart = isCartFilled && !asPath.includes(cartLink);
        setShouldRenderCartFab(showCart);
    }, [isCartFilled, asPath, cartLink]);


    // Set a dynamic scroll padding so that the scroll target is not below the header
    useEffect(() => {
        if (!document || navHeight === undefined) return;
        document.body.style.scrollPaddingTop = navHeight;
        document.documentElement.style.scrollPaddingTop = navHeight;
    }, [navHeight]);

    const pageContentType = getPageContent(props);

    return (
        <React.Fragment key={placement_topnavi}>
            <Box component="header" sx={pageHeaderSx}>
                {
                    props.isHideNavi ? <LogoOnlyNavigation navigation={props.navigation} page={props.page}/> :
                        <>
                            {renderTopNavi(props, greaterThanWindowLarge)}
                            {renderNavigation(props, header)}
                        </>
                }
            </Box>
            <Box
                component='main'
                id='grid'
                sx={pageGridItemFullWidthSx}>
                {
                    renderPageContent(props, pageContentType, props.isHideNavi)
                }
                {
                    renderPlacements(props, pageContentType)
                }
            </Box>
            {renderFooter(props)}
            {shouldRenderCartFab && <CCOCartFab/>}
        </React.Fragment>
    );
}

export function renderTopNavi(props: Props, breakPointLg: boolean) {
    return breakPointLg ? <TopNavi empty={props.isHideNavi}
                                   page={props.page}
                                   navigation={props.navigation}
                                   header={props.page?.grid.placements.find((p: PageGridPlacementType) => p.name === placement_header)}/> : null
}

function renderNavigation(props: Props, header: any) {
    return <Navigation navigation={props.navigation} page={props.page} header={header}/>
}

export function renderFooter(props: Props, isEasyLangLayout?: boolean) {
    const footer = props.page?.grid.placements.find((p: PageGridPlacementType) => p.name === placement_footer);
    if (!footer) {
        return null;
    }
    return isEasyLangLayout ?
        <EasyLangFooter CAEGlobalSettings={props.CAEGlobalSettings} items={footer.items}/> :
        <Footer CAEGlobalSettings={props.CAEGlobalSettings} items={footer.items} page={props.page}
                isHideNavi={props.isHideNavi}/>
}

export function renderPageContent(props: Props, pageContentType: PageContentType, isHideNavi?: boolean) {
    switch (pageContentType) {
        case PageContentType.Product:
            return <P020DonationProduct product={props.page} CAEGlobalSettings={props.CAEGlobalSettings}/>
        case PageContentType.Cart:
            return renderCartContent(props);
        case PageContentType.Standard:
            return renderStandardContent(props, isHideNavi);
        case PageContentType.Special:
            return renderSpecialContent(props);
        default:
            return null;
    }
}

export function renderStandardContent(props: Props, isHideNavi?: boolean, isEasyLangLayout?: boolean) {
    const isOffer = props.page && isOfferPage(props.page);
    const hasContent = props.page && (props.page.teaserText || props.page.detailText || props.page.title);
    const hasSpecialTopicPlaceholder = checkForSpecialTopics(props.page);
    const pageContainsQueryList = checkForQueryList(props.page);
    const isH1Activated: boolean = !props.page.settings.deactivateH1;
    if (!(hasContent || hasSpecialTopicPlaceholder || isOffer || pageContainsQueryList)) {
        return null;
    }
    const align = isEasyLangLayout ? 'left' : 'center';
    return (
        <React.Fragment>
            {props.page.grid.placements.map((p: PageGridPlacementType, index: number) =>
                renderPlacement(p, index, props.CAEGlobalSettings, props.csrfToken, false, "stageteaser", props.page, props.pageSettings))}
            {(props.page && !isHideNavi) && !isEasyLangLayout && <Breadcrumb target={props.page}/>}
            <CCOComponentWrapper customStyle={{marginBottom: {xl: 12, lg: 12, md: 12, sm: 6, xs: 6}}} id='main'>
                <CCOContainer dataCypress='standardPlacement' width='small'>
                    <PreviewElementBoxDrawer propertyId={props.page.id}>
                        <CCOH1 metadataId={props.page.id}
                               propertyName='properties.title'
                               title={props.page?.title}
                               isH1Activated={isH1Activated}
                               align={align}/>
                        {
                            renderRichText(props.page.detailText, props.page.teaserText, props.page.id, props.pageSettings?.ignoreTeaserText ?? false, isHideNavi, props.CAEGlobalSettings)
                        }
                    </PreviewElementBoxDrawer>
                </CCOContainer>
            </CCOComponentWrapper>
        </React.Fragment>)
}

function renderRichText(detailText: any, teaserText: any, metadataId: any, ignoreTeaserText: boolean, isHideNavi: boolean | undefined, CAEGlobalSettings: CAEGlobalSettings) {
    if (ignoreTeaserText) {
        return <></>;
    }
    if (detailText) {
        return <CCORichText metadataId={metadataId} textAsTree={detailText.textAsTree} isHideNavi={isHideNavi}
                            propertyName={'properties.detailText'} caeGlobalSettings={CAEGlobalSettings}/>
    }
    if (teaserText) {
        return <CCORichText metadataId={metadataId} textAsTree={teaserText.textAsTree} isHideNavi={isHideNavi}
                            propertyName={'properties.teaserText'} caeGlobalSettings={CAEGlobalSettings}/>
    }
    return <></>;
}

export function renderPlacements(props: Props, pageContentType: PageContentType) {
    const isStandardContent = pageContentType === PageContentType.Standard;
    const credentials = props.credentials;
    return props.page.grid.placements.map((p: PageGridPlacementType, index: number) =>
        renderPlacement(p,
            index,
            props.CAEGlobalSettings,
            props.csrfToken,
            isStandardContent,
            undefined,
            props.page,
            props.pageSettings,
            credentials,
            props.donationInitializerData));
}

function renderPlacement(placement: PageGridPlacementType,
                         index: number,
                         CAEGlobalSettings: any,
                         csrfToken: string,
                         isStandardContent?: boolean,
                         placementName?: string,
                         page?: Page,
                         pageSettings?: any,
                         credentials?: any,
                         donationInitializerData?: {
                             queryData: any;
                             isProduct: boolean;
                             contentId: string
                         } | null) {
    switch (placement.name) {
        case placement_header: {
            return null
        }
        case placement_stageteaser: {
            if (placementName && placementName === placement.name) {
                return <PageGridPlacement placementName={placement_stageteaser} key={index} items={placement.items}
                                          viewtype={placement.viewtype || ''}
                                          CAEGlobalSettings={CAEGlobalSettings} csrfToken={csrfToken}
                                          donationInitializerData={donationInitializerData ?? null}/>
            } else return null
        }
        case placement_footer: {
            return null
        }
        default: {
            if (isStandardContent) {
                return <PageGridPlacement placementName={placement.name} key={index} items={placement.items}
                                          viewtype={placement.viewtype || ''}
                                          CAEGlobalSettings={CAEGlobalSettings} csrfToken={csrfToken}
                                          placeholderContent={page && isSpecialTopicPage(page) ? (page) : undefined}
                                          pageSettings={pageSettings} credentials={credentials}
                                          donationInitializerData={donationInitializerData ?? null}
                />

            } else return null
        }
    }
}

export function renderSpecialContent(props: Props) {
    const content = props?.content?.content?.content;
    switch (content?.type) {
        case sosEvent:
            return <P014Event sosEvent={content} csrfToken={props.csrfToken}
                              CAEGlobalSettings={props.CAEGlobalSettings} page={props.page}/>
        case sosNews:
            return <P013News sosNews={content} csrfToken={props.csrfToken}
                             CAEGlobalSettings={props.CAEGlobalSettings}/>
        case sosPressrelease:
            return <P012Pressrelease sosPressrelease={content} csrfToken={props.csrfToken}
                                     CAEGlobalSettings={props.CAEGlobalSettings}/>
        case sosPublication:
            return <P011Publication sosPublication={content} backUri={props.page.selfUrl}/>
        default:
            return null;
    }
}

function renderCartContent(props: Props) {
    return isCart(props.currentURL) ? <P015Cart page={props.page} pageSettings={props.pageSettings}/> : ""
}

export default HomepageLayout